import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getInvoices,
  getInvoiceSummary,
  getInvoice,
  getTasks,
  getBillingEntities,
} from '../../api';
import {
  Invoice,
  InvoiceStatus,
  StatusBadge,
  outstandingOf,
  PaymentModal,
  SendInvoiceEmailModal,
  InvoiceCreateWizard,
  GSTPreviewModal,
} from '../Invoices';
import { formatRupeesUI, formatDateDdMmmYy } from '../../utils/format';
import { useCanSeeBilling } from '../../utils/billingAccess';
import MobileReceivables from './MobileReceivables';

/**
 * Invoicing on a phone.
 *
 * The desktop page carries six tabs — receivables, TDS, reconciliation,
 * credit notes, receipts, profit centres — all of them wide analytical
 * tables. Only one of those earns a place here: receivables, because chasing
 * payment is the thing partners do away from a desk. It rides a segmented
 * control rather than a sixth bottom tab — five is the practical ceiling —
 * and is hidden outright from staff, whose /receivables requests would only
 * ever 403.
 *
 * The rest is the invoice register plus the two things worth doing away from
 * a desk: seeing what's outstanding, and taking the money.
 *
 * The heavy lifting is reused rather than reimplemented: the payment modal,
 * the send-email modal and the create wizard are the same components the
 * desktop uses, so billing rules (GST, TDS, credit notes, numbering) can
 * never drift between the two.
 */

const FILTERS: Array<{ id: string; label: string }> = [
  { id: '', label: 'All' },
  { id: 'SENT', label: 'Sent' },
  { id: 'OVERDUE', label: 'Overdue' },
  { id: 'PARTIALLY_PAID', label: 'Part paid' },
  { id: 'PAID', label: 'Paid' },
  { id: 'DRAFT', label: 'Draft' },
];

const PAGE_SIZE = 20;

const MobileInvoices: React.FC = () => {
  const location = useLocation();
  const canSeeBilling = useCanSeeBilling();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [summary, setSummary] = useState({ outstanding: 0, overdueCount: 0 });
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState<'invoices' | 'receivables'>('invoices');
  const [recvKey, setRecvKey] = useState(0);
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [payTarget, setPayTarget] = useState<Invoice | null>(null);
  const [emailTarget, setEmailTarget] = useState<Invoice | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<Invoice | null>(null);
  const [loadingFull, setLoadingFull] = useState(false);

  // The wizard needs these lists; they're only fetched when it's opened, so
  // the register itself stays a two-request screen on a phone connection.
  const [wizardData, setWizardData] = useState<{ tasks: any[]; billingEntities: any[] } | null>(null);
  const [wizardLoading, setWizardLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const params = useCallback(() => {
    const p: any = { page, pageSize: PAGE_SIZE, sortField: 'invoiceDate', sortDir: 'desc' };
    if (status) p.status = status;
    else p.excludeCancelled = '1';
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [page, status, debouncedSearch]);

  const load = useCallback(() => {
    setLoading(true);
    getInvoices(params())
      .then((r) => {
        const rows = r.data.rows ?? [];
        // Page 1 replaces; later pages append, because "Load more" is the
        // right control on a phone and page numbers are not.
        setInvoices((prev) => (page === 1 ? rows : [...prev, ...rows]));
        setTotal(r.data.total ?? 0);
      })
      .catch(() => {
        if (page === 1) setInvoices([]);
      })
      .finally(() => setLoading(false));
  }, [params, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getInvoiceSummary(status ? { status } : { excludeCancelled: '1' })
      .then((r) => setSummary({ outstanding: r.data?.outstanding ?? 0, overdueCount: r.data?.overdueCount ?? 0 }))
      .catch(() => {});
  }, [status]);

  // A deep link from a reminder email (/invoices?invoiceId=12) should open
  // that invoice, not just the list it happens to be on.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('invoiceId');
    if (!id) return;
    const match = invoices.find((i) => i.id === Number(id));
    if (match) setSelected(match);
  }, [location.search, invoices]);

  const reload = () => {
    if (page === 1) load();
    else setPage(1);
    setSelected(null);
    // Remount the receivables view so a payment recorded from its drill-down
    // is reflected in the ageing straight away.
    setRecvKey((k) => k + 1);
  };

  const openWizard = async () => {
    if (wizardData) {
      setShowWizard(true);
      return;
    }
    setWizardLoading(true);
    try {
      const [t, be] = await Promise.all([getTasks(), getBillingEntities()]);
      setWizardData({
        tasks: (t.data || []).filter((x: any) => x.status === 'OPEN' || x.status === 'CLOSED'),
        billingEntities: be.data || [],
      });
      setShowWizard(true);
    } catch {
      alert('Could not load the data needed to raise an invoice. Check your connection and try again.');
    } finally {
      setWizardLoading(false);
    }
  };

  const changeFilter = (id: string) => {
    setStatus(id);
    setPage(1);
  };

  // Receivables rows carry only what the ageing pivot needs; the detail sheet
  // and the payment modal want payments, TDS section and current status, so
  // the full invoice is fetched — the same thing the desktop tab does.
  const openInvoiceById = async (id: number) => {
    try {
      const res = await getInvoice(id);
      setSelected(res.data);
    } catch {
      alert('Could not load that invoice. Check your connection and try again.');
    }
  };

  /**
   * The list endpoint returns a trimmed invoice — no line items, no payments,
   * no billing entity — so the summary opens instantly from the row the user
   * tapped and the full record arrives behind it. Anything that needs the
   * whole invoice (the details section, the PDF) waits on that fetch rather
   * than rendering a half-empty document.
   */
  const openFromList = (inv: Invoice) => {
    setSelected(inv);
    setLoadingFull(true);
    getInvoice(inv.id)
      .then((res) => setSelected((cur) => (cur?.id === inv.id ? res.data : cur)))
      .catch(() => {})
      .finally(() => setLoadingFull(false));
  };

  return (
    <div className="space-y-4">
      {canSeeBilling && (
        <div
          role="tablist"
          aria-label="Invoice view"
          className="relative grid grid-cols-2 p-1 bg-gray-100 rounded-xl"
        >
          {/* Same sliding pill as the login screen: one moving indicator reads
              as the control changing state, where two swapping backgrounds
              read as a repaint. */}
          <span
            aria-hidden="true"
            className="absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-lg bg-white shadow-sm transition-transform duration-200 ease-out motion-reduce:transition-none"
            style={{ transform: view === 'receivables' ? 'translateX(100%)' : 'translateX(0)' }}
          />
          {([
            { key: 'invoices', label: 'Invoices' },
            { key: 'receivables', label: 'Receivables' },
          ] as const).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={view === t.key}
              onClick={() => setView(t.key)}
              className={`relative z-10 py-3 text-sm font-medium rounded-lg transition-colors ${
                view === t.key ? 'text-gray-900' : 'text-gray-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {view === 'receivables' && canSeeBilling ? (
        <MobileReceivables key={recvKey} onOpenInvoice={openInvoiceById} />
      ) : (
        <>
        {canSeeBilling && (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-white border border-gray-200 rounded-xl">
              <p className="text-xs text-gray-500">Outstanding</p>
              <p className="text-lg font-bold text-gray-900 mt-0.5 truncate">
                {formatRupeesUI(summary.outstanding)}
              </p>
            </div>
            <div className="p-3 bg-white border border-gray-200 rounded-xl">
              <p className="text-xs text-gray-500">Overdue</p>
              <p className={`text-lg font-bold mt-0.5 ${summary.overdueCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {summary.overdueCount}
              </p>
            </div>
          </div>
        )}

        <input
          type="search"
          className="input-field py-2.5"
          placeholder="Search invoice no. or client"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />

        {/* Horizontally scrollable chips: six filters will never fit across
            375px, and wrapping them costs two rows of vertical space. */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.id || 'all'}
              onClick={() => changeFilter(f.id)}
              className={`shrink-0 px-3 py-2 rounded-full text-xs font-medium border transition-colors ${
                status === f.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          {loading && invoices.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
          ) : invoices.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">No invoices found</p>
          ) : (
            invoices.map((inv) => {
              const due = outstandingOf(inv);
              return (
                <button
                  key={inv.id}
                  onClick={() => openFromList(inv)}
                  className="w-full text-left p-4 bg-white border border-gray-200 rounded-xl active:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{inv.invoiceNumber}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {inv.client?.clientName || inv.task?.client?.clientName || '—'}
                      </p>
                    </div>
                    <StatusBadge status={inv.status as InvoiceStatus} />
                  </div>
                  <div className="flex items-baseline justify-between gap-3 mt-3">
                    <span className="text-xs text-gray-500">{formatDateDdMmmYy(inv.invoiceDate)}</span>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">
                        {formatRupeesUI(inv.totalAmount ?? inv.amount)}
                      </p>
                      {due > 0 && (
                        <p className="text-[11px] text-red-600 mt-0.5">{formatRupeesUI(due)} due</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}

          {invoices.length < total && (
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={loading}
              className="w-full py-3 text-sm font-medium text-blue-600 bg-white border border-gray-200 rounded-xl disabled:text-gray-400"
            >
              {loading ? 'Loading…' : `Load more (${total - invoices.length} left)`}
            </button>
          )}
        </div>

        {canSeeBilling && (
          <button
            onClick={openWizard}
            disabled={wizardLoading}
            className="fixed right-4 z-30 w-14 h-14 rounded-full bg-blue-600 text-white text-2xl shadow-lg flex items-center justify-center active:bg-blue-700 disabled:bg-blue-300"
            style={{ bottom: 'calc(4.5rem + env(safe-area-inset-bottom))' }}
            aria-label="New invoice"
          >
            {wizardLoading ? '…' : '+'}
          </button>
        )}
        </>
      )}

      {selected && (
        <InvoiceSheet
          invoice={selected}
          onClose={() => setSelected(null)}
          onPay={() => setPayTarget(selected)}
          onEmail={() => setEmailTarget(selected)}
          onPreview={() => setPreviewTarget(selected)}
          canSeeBilling={canSeeBilling}
          loadingFull={loadingFull}
        />
      )}

      {/* The desktop's own preview: the invoice rendered exactly as the PDF
          prints it, with download and print. Reused rather than rebuilt so a
          phone can never show a document the client wouldn't receive. */}
      {previewTarget && (
        <GSTPreviewModal invoice={previewTarget} onClose={() => setPreviewTarget(null)} />
      )}

      {payTarget && (
        <PaymentModal
          invoice={payTarget}
          canEdit={canSeeBilling}
          onClose={() => setPayTarget(null)}
          onSaved={() => {
            setPayTarget(null);
            reload();
          }}
        />
      )}

      {emailTarget && (
        <SendInvoiceEmailModal
          invoice={emailTarget}
          onClose={() => setEmailTarget(null)}
          onSent={() => {
            setEmailTarget(null);
            reload();
          }}
        />
      )}

      {showWizard && wizardData && (
        <InvoiceCreateWizard
          tasks={wizardData.tasks}
          billingEntities={wizardData.billingEntities}
          onClose={() => setShowWizard(false)}
          onCreated={() => {
            setShowWizard(false);
            reload();
          }}
        />
      )}
    </div>
  );
};

// ── Detail sheet ─────────────────────────────────────────────────────────────

const Row: React.FC<{ label: string; value: React.ReactNode; strong?: boolean }> = ({
  label,
  value,
  strong,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-2">
    <span className="text-sm text-gray-500 shrink-0">{label}</span>
    <span className={`text-sm text-right ${strong ? 'font-bold text-gray-900' : 'text-gray-800'}`}>
      {value}
    </span>
  </div>
);

const InvoiceSheet: React.FC<{
  invoice: Invoice;
  onClose: () => void;
  onPay: () => void;
  onEmail: () => void;
  onPreview: () => void;
  canSeeBilling: boolean;
  loadingFull: boolean;
}> = ({ invoice: inv, onClose, onPay, onEmail, onPreview, canSeeBilling, loadingFull }) => {
  // Summary first, the rest behind a tap. The figures that decide what you do
  // next — total, outstanding, status — are what a phone is opened for; the
  // GST breakdown and line items are for when someone asks a question about
  // the invoice, which is rarer and worth a second tap.
  const [expanded, setExpanded] = useState(false);

  const due = outstandingOf(inv);
  const tax =
    Number(inv.cgstAmount ?? 0) + Number(inv.sgstAmount ?? 0) + Number(inv.igstAmount ?? 0);
  const lineItems = inv.lineItems ?? [];
  const payments = inv.payments ?? [];

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-t-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate">{inv.invoiceNumber}</p>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {inv.client?.clientName || inv.task?.client?.clientName || '—'}
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-2 divide-y divide-gray-100">
          <Row label="Status" value={<StatusBadge status={inv.status as InvoiceStatus} />} />
          <Row label="Invoice date" value={formatDateDdMmmYy(inv.invoiceDate)} />
          {inv.dueDate && <Row label="Due date" value={formatDateDdMmmYy(inv.dueDate)} />}
          <Row label="Taxable value" value={formatRupeesUI(inv.amount)} />
          {tax > 0 && <Row label="GST" value={formatRupeesUI(tax)} />}
          <Row label="Invoice total" value={formatRupeesUI(inv.totalAmount ?? inv.amount)} strong />
          {Number(inv.tdsAmount ?? 0) > 0 && (
            <Row label="TDS deducted" value={`− ${formatRupeesUI(inv.tdsAmount)}`} />
          )}
          {Number(inv.paymentAmount ?? 0) > 0 && (
            <Row label="Received" value={`− ${formatRupeesUI(inv.paymentAmount)}`} />
          )}
          {Number(inv.creditedAmount ?? 0) > 0 && (
            <Row label="Credit notes" value={`− ${formatRupeesUI(inv.creditedAmount)}`} />
          )}
          {Number(inv.writtenOffAmount ?? 0) > 0 && (
            <Row label="Written off" value={`− ${formatRupeesUI(inv.writtenOffAmount)}`} />
          )}
          <Row
            label="Outstanding"
            value={<span className={due > 0 ? 'text-red-600' : 'text-green-700'}>{formatRupeesUI(due)}</span>}
            strong
          />

          {!expanded ? (
            <button
              onClick={() => setExpanded(true)}
              className="w-full py-3.5 text-sm font-medium text-blue-600 text-left"
            >
              {loadingFull ? 'Loading full details…' : 'See full details'}
            </button>
          ) : (
            <>
              {inv.billingEntity?.name && <Row label="Billed by" value={inv.billingEntity.name} />}
              {(inv.billingEntity as any)?.gstin && (
                <Row label="Our GSTIN" value={(inv.billingEntity as any).gstin} />
              )}
              {inv.clientGstin?.gstin && <Row label="Client GSTIN" value={inv.clientGstin.gstin} />}
              {(inv.clientGstin as any)?.stateName && (
                <Row label="Place of supply" value={(inv.clientGstin as any).stateName} />
              )}
              <Row label="Tax type" value={String(inv.taxType).replace(/_/g, ' + ')} />
              {inv.rcmApplicable && <Row label="Reverse charge" value="Applicable" />}
              {Number(inv.cgstAmount ?? 0) > 0 && (
                <Row label={`CGST @ ${inv.cgstRate ?? 0}%`} value={formatRupeesUI(inv.cgstAmount)} />
              )}
              {Number(inv.sgstAmount ?? 0) > 0 && (
                <Row label={`SGST @ ${inv.sgstRate ?? 0}%`} value={formatRupeesUI(inv.sgstAmount)} />
              )}
              {Number(inv.igstAmount ?? 0) > 0 && (
                <Row label={`IGST @ ${inv.igstRate ?? 0}%`} value={formatRupeesUI(inv.igstAmount)} />
              )}
              {inv.tdsSection && (
                <Row label="TDS section" value={`${inv.tdsSection} @ ${inv.tdsRate ?? 0}%`} />
              )}
              {inv.hsnSacCode && <Row label="HSN / SAC" value={inv.hsnSacCode} />}
              {inv.task?.taskName && <Row label="Task" value={inv.task.taskName} />}
              {inv.profitCentre?.name && <Row label="Profit centre" value={inv.profitCentre.name} />}
              {inv.signatory?.name && <Row label="Signed by" value={inv.signatory.name} />}
              {inv.notes && <Row label="Notes" value={inv.notes} />}

              {lineItems.length > 0 && (
                <div className="py-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Line items
                  </p>
                  <div className="space-y-2">
                    {lineItems.map((li, i) => (
                      <div key={li.id ?? i} className="bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-sm text-gray-800 flex-1">{li.description}</p>
                          <span className="text-sm font-medium text-gray-900 shrink-0">
                            {formatRupeesUI(li.amount)}
                          </span>
                        </div>
                        {(Number(li.quantity ?? 0) > 1 || li.hsnSac) && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            {Number(li.quantity ?? 0) > 1
                              ? `${li.quantity} ${li.unit || ''} × ${formatRupeesUI(li.rate)}`
                              : ''}
                            {li.hsnSac
                              ? `${Number(li.quantity ?? 0) > 1 ? ' · ' : ''}HSN ${li.hsnSac}`
                              : ''}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {payments.length > 0 && (
                <div className="py-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Payments received
                  </p>
                  <div className="space-y-2">
                    {payments.map((p) => (
                      <div key={p.id} className="bg-gray-50 rounded-lg px-3 py-2">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-sm text-gray-800">
                            {formatDateDdMmmYy(p.paymentDate)}
                          </span>
                          <span className="text-sm font-medium text-green-700">
                            {formatRupeesUI(p.amount)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {p.mode}
                          {p.reference ? ` · ${p.reference}` : ''}
                          {Number(p.tdsDeducted ?? 0) > 0
                            ? ` · TDS ${formatRupeesUI(p.tdsDeducted)}`
                            : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => setExpanded(false)}
                className="w-full py-3.5 text-sm font-medium text-gray-500 text-left"
              >
                Show less
              </button>
            </>
          )}
        </div>

        <div className={`px-5 py-4 border-t border-gray-100 grid gap-2 ${canSeeBilling ? 'grid-cols-3' : 'grid-cols-2'}`}>
          <button
            onClick={onPreview}
            className="py-3 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 active:bg-gray-200"
          >
            PDF
          </button>
          <button
            onClick={onEmail}
            className="py-3 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 active:bg-gray-200"
          >
            Email
          </button>
          {canSeeBilling && (
            <button
              onClick={onPay}
              className="py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 active:bg-blue-700"
            >
              {due > 0 ? 'Payment' : 'Payments'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileInvoices;
